import type {
    AmazonPlaybackQuality,
    QualitySelectionReason,
    StreamingQuality,
    TrackNerdStats,
} from '@amzn/vinyl-amzn'
import { data, type ObservableValue, type MutableValue } from '@amazon/vinyl-observable'
import type { AnyRecord, ReadonlyEventHost } from '@amazon/vinyl-util'
import type { PlaylistEntry } from '@/playback/PlaylistController'

/*
 * Exposed playback interfaces. `Player` is the engine-agnostic observable
 * abstraction every consumer codes against; the Vinyl-only quality surface lives
 * off {@link AmznPlaybackQualitySource}. See `player-refactor-hld.md`.
 * @module
 */

/**
 * A playback error surfaced to the UI (e.g. the error toast in
 * `playerPlaylistBinding`). Engine-neutral: a normalized code plus the
 * originating cause, so a sub-player can attach engine detail without leaking
 * its types onto the base interface.
 */
export interface PlaybackError {
    readonly code: string
    readonly message: string
    readonly cause?: unknown
}

/**
 * Amazon playback quality/nerd-stats surface, kept OFF the base observable state so
 * {@link Player} stays engine-neutral. Only the Vinyl-aware consumers (quality
 * badge, nerd-stats, streaming-quality binding) depend on this; the delegate
 * exposes it via {@link Player.amznPlaybackQuality}, which null-objects when a non-Vinyl
 * engine is active.
 */
export interface AmznPlaybackQualitySource {
    readonly playbackQuality$: ObservableValue<AmazonPlaybackQuality | null>
    readonly trackNerdStats$: ObservableValue<TrackNerdStats | null>
    getQualitySelectionReason(): QualitySelectionReason | null
    setStreamingQuality(quality: StreamingQuality, abrEnabled: boolean): void
}

/**
 * Engine-neutral description of one caption/subtitle track. Deliberately NOT the
 * engine's `TextTrackInfo` (the installed engine doesn't ship one — the text-track
 * API lives on the unlanded `feat/text-tracks` Vinyl branch), so the caption
 * surface stays dependency-free and its backing can swap without touching this
 * type. `id` is a per-source-stable handle used by {@link CaptionSource.setActiveTextTrack}.
 */
export interface TextTrackInfo {
    readonly id: string
    readonly label: string
    readonly language: string
    readonly kind: string
}

/**
 * Closed-caption surface, kept OFF the base observable state so {@link Player}
 * stays engine-neutral (mirrors {@link AmznPlaybackQualitySource}). The delegate
 * exposes it via {@link Player.captions}, null-objecting when a non-caption engine
 * is active. `captionsEnabled$` / `preferredLanguage$` are session preferences the
 * UI writes; `textTracks$` / `activeTextTrack$` reflect the current source's tracks.
 */
export interface CaptionSource {
    readonly textTracks$: ObservableValue<readonly TextTrackInfo[]>
    readonly activeTextTrack$: ObservableValue<TextTrackInfo | null>
    readonly captionsEnabled$: MutableValue<boolean>
    readonly preferredLanguage$: MutableValue<string | null>
    /** Show the track with this id (others off); null turns captions off. */
    setActiveTextTrack(id: string | null): void
}

/**
 * Engine-neutral description of one chapter/section: its `title`, the engine's
 * section `index` — the value {@link SectionNavSource.currentIndex$} reports for
 * the playing chapter and {@link SectionNavSource.jumpToSection} accepts — and
 * its `duration` in seconds. Deliberately NOT the Audible SDK's SectionMetadataItem
 * (mirrors {@link TextTrackInfo}), so the chapter-list drawer stays dependency-free.
 */
export interface SectionInfo {
    readonly index: number
    readonly title: string
    readonly duration: number
}

/**
 * Chapter/section navigation surface, kept OFF the base observable state so
 * {@link Player} stays engine-neutral (mirrors {@link CaptionSource}). Only the
 * chapter-structured Audible engine backs this with real section stepping; every
 * other engine null-objects it. `currentIndex$` of -1 (or `count$` of 0) means
 * "no sections". `currentTitle$` is the live chapter title the transport shows as
 * the audiobook's primary line (null until a chapter resolves).
 * `getPositionInSection` returns section-relative seconds, used by the
 * transport's within-3s previous rule. `getSupplementalPdfUrl` resolves the
 * book's accompanying-PDF URL (null off Audible or when the book carries none) —
 * an audiobook-only read that rides this Audible-backed surface rather than
 * widening the flat {@link Player}. `getSections` is the chapter list the drawer
 * renders (empty until the book's section metadata resolves) and
 * `jumpToSection` seeks playback to a chapter by its {@link SectionInfo.index}.
 */
export interface SectionNavSource {
    readonly currentIndex$: ObservableValue<number>
    readonly count$: ObservableValue<number>
    readonly currentTitle$: ObservableValue<string | null>
    nextSection(): Promise<void> | void
    previousSection(): Promise<void> | void
    getPositionInSection(): number
    getSupplementalPdfUrl(): Promise<string | null>
    getSections(): readonly SectionInfo[]
    jumpToSection(index: number): Promise<void> | void
}

/**
 * The HLS-interstitial ad currently playing. `index` is zero-based within its
 * break; `skipIn` is null when no skip window is pending; `currentTime` /
 * `duration` are the ad's own timeline in seconds (`duration` 0 when unknown).
 */
export interface AdState {
    readonly index: number
    readonly count: number
    readonly canSkip: boolean
    readonly skipIn: number | null
    readonly currentTime: number
    readonly duration: number
}

/**
 * Ad surface, kept OFF the base observable state (mirrors {@link CaptionSource}).
 * `adState$` is null while primary content plays. `skipAd` skips the current ad;
 * the engine doesn't enforce skip restrictions, so callers gate on `canSkip`.
 */
export interface AdSource {
    readonly adState$: ObservableValue<AdState | null>
    skipAd(): void
}

/**
 * Why a seek was issued; an engine may treat a seek to a stored resume point, or the transport's
 * Previous restarting the current entry, specially.
 */
export interface SeekOptions {
    readonly reason?: 'resume' | 'restart'
}

/**
 * The discrete moments a {@link Player} emits, named and timed as Vinyl's player events. State
 * that persists is a `$` cell instead; ad events belong to {@link AdSource}, not here.
 */
export interface PlayerEventMap {
    readonly play: AnyRecord
    readonly playing: AnyRecord
    readonly pause: AnyRecord
    readonly waiting: AnyRecord
    readonly seeking: AnyRecord
    readonly seeked: AnyRecord
    readonly emptied: AnyRecord
    /** The media reached its end; fires for each ad too. */
    readonly ended: AnyRecord
    /** The current entry, including any postroll ads, finished playing. Fires once per entry. */
    readonly trackEnded: AnyRecord
}

export const ALL_PLAYER_EVENTS = [
    'play',
    'playing',
    'pause',
    'waiting',
    'seeking',
    'seeked',
    'emptied',
    'ended',
    'trackEnded',
] as const satisfies readonly (keyof PlayerEventMap)[]

/**
 * Engine-agnostic player: observable playback state, mutable controls, the
 * minimal command set, and the {@link PlayerEventMap} events. Every getter+event
 * pair of the legacy Vinyl slice collapses into a single observable cell, so
 * consumers subscribe once and never re-subscribe across a sub-player switch
 * (see `DelegatingPlayer`).
 */
export interface Player extends ReadonlyEventHost<PlayerEventMap> {
    /** Actively producing audio. */
    readonly playing$: ObservableValue<boolean>
    /** Loaded but halted. */
    readonly paused$: ObservableValue<boolean>
    /**
     * Whether the last command was to play rather than pause: unlike {@link paused$}, it holds
     * through a load, a stall or a failed play.
     */
    readonly playRequested$: ObservableValue<boolean>
    /**
     * The media's playhead sits at its end, as Vinyl's `player.ended`: set by `ended` (also at
     * each ad's end), cleared by a seek, a replay or a source change. For "the entry finished"
     * listen to `trackEnded`.
     */
    readonly ended$: ObservableValue<boolean>
    /**
     * Play is intended but the engine can't yet play through — i.e. show a
     * spinner. True while a play is pending (requested, not yet started) or a
     * playing session stalls, until buffered enough to play through. False before
     * any play gesture, so a native-HLS track sitting at HAVE_METADATA (which on
     * Safari won't advance to HAVE_ENOUGH_DATA until a user gesture unlocks it)
     * never spins before the user hits play.
     */
    readonly loading$: ObservableValue<boolean>
    /** Playhead position, seconds; the ad's own clock during an interstitial. */
    readonly currentTime$: ObservableValue<number>
    /** Total duration, seconds (NaN before metadata); the ad's own during an interstitial. */
    readonly duration$: ObservableValue<number>
    /**
     * Content playhead, seconds: tracks {@link currentTime$}, but holds the
     * content position through an ad. Read this for any content-progress report.
     */
    readonly contentTime$: ObservableValue<number>
    /** Content duration, seconds: tracks {@link duration$}, but holds through an ad. */
    readonly contentDuration$: ObservableValue<number>
    /** Playhead as a 0..1 fraction of duration. */
    readonly currentTimePercent$: ObservableValue<number>
    /** Buffered-ahead as a 0..1 fraction of duration. */
    readonly fetchedTimePercent$: ObservableValue<number>

    /** Output volume, 0..1. */
    readonly volume$: MutableValue<number>
    /** Muted flag. */
    readonly muted$: MutableValue<boolean>
    /** Playback speed multiplier. */
    readonly playbackRate$: MutableValue<number>
    /** Loop the current entity. */
    readonly loop$: MutableValue<boolean>

    /** Latest playback error, or null when healthy. */
    readonly error$: ObservableValue<PlaybackError | null>

    /** Amazon playback quality/nerd-stats surface for the active engine (null-object off Vinyl). */
    readonly amznPlaybackQuality: AmznPlaybackQualitySource

    /** Closed-caption surface for the active engine (null-object when captions aren't supported). */
    readonly captions: CaptionSource

    /** Chapter/section navigation for the active engine (null-object off Audible). */
    readonly sections: SectionNavSource

    /** Ad state + skip for the active engine (null-object when the engine plays no ads). */
    readonly ads: AdSource

    play(): Promise<void>
    pause(): void
    seekTo(seconds: number, options?: SeekOptions): Promise<void>

    /**
     * Warm `entry`'s media on its engine WITHOUT loading it as the current
     * track or beginning playback, so the user's eventual play starts faster.
     * Side-effect-free relative to the transport: no `current`-track change and
     * no metrics — it only pre-populates the engine's track cache, which a later
     * `activate`/`load` of the same uri reuses. A no-op on engines that can't pre-warm.
     */
    preload(entry: PlaylistEntry): void

    /** Release all engine resources and observable cells; app-teardown only. */
    dispose(): void
}

/**
 * Idle {@link AmznPlaybackQualitySource} for engines that expose no Vinyl quality (a
 * cast bridge or a static render). Every cell holds an idle default and never
 * changes; `setStreamingQuality` is a no-op.
 */
export const NULL_AMZN_PLAYBACK_QUALITY: AmznPlaybackQualitySource = {
    playbackQuality$: data<AmazonPlaybackQuality | null>(null),
    trackNerdStats$: data<TrackNerdStats | null>(null),
    getQualitySelectionReason: () => null,
    setStreamingQuality: () => {},
}

/**
 * Idle {@link CaptionSource} for engines that surface no captions (Audible, cast
 * bridge, static render). The track lists stay empty and `setActiveTextTrack` is
 * a no-op; the preference cells are still writable so shared UI can bind them
 * uniformly across engines.
 */
export const NULL_CAPTION_SOURCE: CaptionSource = {
    textTracks$: data<readonly TextTrackInfo[]>([]),
    activeTextTrack$: data<TextTrackInfo | null>(null),
    captionsEnabled$: data(false),
    preferredLanguage$: data<string | null>(null),
    setActiveTextTrack: () => {},
}

/**
 * Idle {@link SectionNavSource} for engines that aren't chapter-structured (Vinyl
 * music/podcast/video, cast bridge, static render). `currentIndex$` -1 + `count$`
 * 0 signal "no sections"; the step methods are no-ops and `getPositionInSection`
 * is 0, so the transport falls back to whole-entry skip for these engines.
 */
export const NULL_SECTION_NAV: SectionNavSource = {
    currentIndex$: data(-1),
    count$: data(0),
    currentTitle$: data<string | null>(null),
    nextSection: () => {},
    previousSection: () => {},
    getPositionInSection: () => 0,
    getSupplementalPdfUrl: () => Promise.resolve(null),
    getSections: () => [],
    jumpToSection: () => {},
}

/** Idle {@link AdSource} for engines that play no ads (Audible, cast bridge, static render). */
export const NULL_AD_SOURCE: AdSource = {
    adState$: data<AdState | null>(null),
    skipAd: () => {},
}
